// src/components/IssueLog.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // <-- MODIFICA: Importa hook

// --- IMPORT MUI (Invariati) ---
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemButton,
  Alert
 } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PostAddIcon from '@mui/icons-material/PostAdd';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

// ── Hook dettatura vocale ────────────────────────────────────────
function useSpeechRecognition(onResult) {
  const [isListening, setIsListening] = React.useState(false);
  const recRef = React.useRef(null);

  React.useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = 'it-IT';
    rec.onresult = (e) => {
      let text = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) text += e.results[i][0].transcript;
      }
      if (text) onResult(text.trim());
    };
    rec.onend = () => setIsListening(false);
    rec.onerror = () => setIsListening(false);
    recRef.current = rec;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => {
    if (!recRef.current) return;
    if (isListening) { recRef.current.stop(); setIsListening(false); }
    else             { recRef.current.start(); setIsListening(true); }
  };

  const supported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  return { isListening, toggle, supported };
}

// --- Componente Modale (MODIFICATO con t()) ---
const AddToWorkOrderModal = ({ open, onClose, teamId, vehicleId, item, onAssign, t, currentLang }) => {
  const [workOrders, setWorkOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && vehicleId && teamId) {
      setIsLoading(true);
      const q = query(
        collection(db, "workOrders"),
        where("teamId", "==", teamId),
        where("vehicleId", "==", vehicleId),
        where("status", "==", "Open")
      );
      const unsub = onSnapshot(q, (snapshot) => {
        setWorkOrders(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setIsLoading(false);
      }, (error) => {
        console.error("Error fetching open work orders:", error);
        setIsLoading(false);
      });
      return () => unsub();
    }
  }, [open, vehicleId, teamId]);

  const handleAssign = async (workOrder) => {
    if (!item) return;
    setIsSaving(true);
    try {
      await onAssign(workOrder, item);
      onClose();
    } catch (error) {
      console.error("Error assigning to W.O.:", error);
      alert(t('modal_assign_error')); // Tradotto
    } finally {
      setIsSaving(false);
    }
  };
  
  // --- MODIFICA: Formattazione data localizzata ---
  const getLocaleFormat = () => (currentLang === 'it' ? 'it-IT' : 'en-GB');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('modal_title')}</DialogTitle>
      <DialogContent>
        {isLoading && <CircularProgress />}
        {!isLoading && workOrders.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('modal_empty')}
          </Alert>
        )}
        {!isLoading && (
          <List>
            {workOrders.map(wo => (
              <ListItem key={wo.id} disablePadding>
                <ListItemButton onClick={() => handleAssign(wo)} disabled={isSaving}>
                  <ListItemIcon><PostAddIcon /></ListItemIcon>
                  <ListItemText 
                    primary={wo.title} 
                    secondary={`${t('modal_created')} ${wo.createdAt ? new Date(wo.createdAt.toDate()).toLocaleDateString(getLocaleFormat()) : '...'}`} 
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
        <Button 
          variant="outlined" 
          onClick={() => navigate('/workshop/new')} 
          sx={{ mt: 2 }}
        >
          {t('modal_new_wo_button')}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>{t('modal_cancel_button')}</Button>
      </DialogActions>
    </Dialog>
  );
};


function IssueLog({ vehicleId, teamId }) {
  // --- MODIFICA: Inizializza t() ---
  const { t, i18n } = useTranslation('issue');
  const currentLang = i18n.language;
  // --- FINE MODIFICA ---

  // Stati (invariati)
  const [issues, setIssues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newIssueDesc, setNewIssueDesc] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isListening, toggle: toggleMic, supported: micSupported } =
    useSpeechRecognition((text) =>
      setNewIssueDesc(prev => prev ? `${prev} ${text}` : text)
    );
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);

  // useEffect (invariato)
  useEffect(() => {
    if (!vehicleId || !teamId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    
    const issuesQuery = query(
      collection(db, "vehicleIssues"),
      where("vehicleId", "==", vehicleId),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(issuesQuery, (querySnapshot) => {
      const issuesList = [];
      querySnapshot.forEach((doc) => {
        issuesList.push({ id: doc.id, ...doc.data() });
      });
      setIssues(issuesList);
      setIsLoading(false);
    }, (error) => {
      console.error("Error issue log listener (Firestore index needed?):", error.message);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [vehicleId, teamId]);

  // --- MODIFICA: Funzioni Handler (con t() per alert) ---
  const handleAddIssue = async (e) => {
    e.preventDefault();
    const description = newIssueDesc.trim();
    if (!description) return;

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "vehicleIssues"), {
        teamId: teamId,
        vehicleId: vehicleId,
        description: description,
        status: 'Aperto', 
        createdAt: serverTimestamp()
      });
      setNewIssueDesc('');
    } catch (error) {
      console.error("Error adding issue:", error);
      alert(t('add_error')); // Tradotto
    }
    setIsSubmitting(false);
  };

  const handleToggleStatus = async (issue, newStatus) => {
    const issueRef = doc(db, "vehicleIssues", issue.id);
    try {
      await updateDoc(issueRef, {
        status: newStatus
      });
    } catch (error) {
      console.error("Error updating status:", error);
      alert(t('status_error')); // Tradotto
    }
  };
  
  // Funzioni modale (invariate)
  const handleOpenModal = (issue) => {
    setSelectedIssue(issue);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedIssue(null);
  };

  const handleAssignToWorkOrder = async (workOrder, issue) => {
    const batch = writeBatch(db);
    
    const newItemRef = doc(collection(db, "workOrders", workOrder.id, "items"));
    batch.set(newItemRef, {
      teamId: teamId,
      workOrderId: workOrder.id,
      source: 'Issue',
      sourceId: issue.id,
      description: issue.description,
      status: "Open",
      createdAt: serverTimestamp(),
      notes: '', parts: '', hours: 0,
    });
    
    const issueRef = doc(db, "vehicleIssues", issue.id);
    batch.update(issueRef, { status: 'In Workshop' });
    
    await batch.commit();
  };
  // --- FINE MODIFICA ---

  // Filtri (invariati)
  const openIssues = issues.filter(issue => issue.status === 'Aperto');
  const inWorkshopIssues = issues.filter(issue => issue.status === 'In Workshop');
  const resolvedIssues = issues.filter(issue => issue.status === 'Risolto');
  
  // --- MODIFICA: Formattazione data localizzata ---
  const getLocaleFormat = () => (currentLang === 'it' ? 'it-IT' : 'en-US');

  return (
    <>
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 },
          borderTop: '1px solid', 
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          borderRadius: 0 
        }}
      >
        
        {/* Section Add Issue (con t()) */}
        <Box component="form" onSubmit={handleAddIssue} sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>{t('title')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center' }}>
            <TextField
              label={t('description_label')}
              value={newIssueDesc}
              onChange={(e) => setNewIssueDesc(e.target.value)}
              required
              disabled={isSubmitting}
              sx={{ flexGrow: 1, width: '100%' }}
              InputProps={micSupported ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={isListening ? 'Ferma dettatura' : 'Dettatura vocale'}>
                      <IconButton
                        onClick={toggleMic}
                        edge="end"
                        color={isListening ? 'error' : 'default'}
                        sx={isListening ? { animation: 'pulse 1s infinite' } : {}}
                      >
                        {isListening ? <MicOffIcon /> : <MicIcon />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              } : undefined}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
              size="large"
              sx={{ height: '56px', width: { xs: '100%', sm: 'auto' } }}
            >
              {t('add_button')}
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />

        {/* Sezione "Open Issues" (con t()) */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>{t('open_title')}</Typography>
          {isLoading && <CircularProgress size={24} />}
          {!isLoading && openIssues.length === 0 && (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {t('open_empty')}
            </Typography>
          )}
          <List dense>
            {openIssues.map((issue) => (
              <ListItem
                key={issue.id}
                divider
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={t('add_to_wo_tooltip')}>
                      <Button 
                        size="small" 
                        onClick={() => handleOpenModal(issue)}
                        startIcon={<PostAddIcon />}
                        color="primary"
                      >
                        {t('add_to_wo_button')}
                      </Button>
                    </Tooltip>
                    <Button 
                      size="small" 
                      onClick={() => handleToggleStatus(issue, 'Risolto')}
                      startIcon={<CheckCircleIcon />}
                      color="success"
                    >
                      {t('resolve_button')}
                    </Button>
                  </Box>
                }
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <RadioButtonUncheckedIcon color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary={issue.description}
                  secondary={`${t('reported_on')} ${issue.createdAt ? new Date(issue.createdAt.toDate()).toLocaleString(getLocaleFormat()) : '...'}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* Sezione "In Workshop" (con t()) */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>{t('workshop_title')}</Typography>
          {!isLoading && inWorkshopIssues.length === 0 && (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {t('workshop_empty')}
            </Typography>
          )}
          <List dense>
            {inWorkshopIssues.map((issue) => (
              <ListItem key={issue.id} divider>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AssignmentTurnedInIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={issue.description}
                  secondary={`${t('workshop_sent_on')} ${issue.createdAt ? new Date(issue.createdAt.toDate()).toLocaleString(getLocaleFormat()) : '...'}`}
                  sx={{ color: 'text.secondary' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Section Resolved Issues (con t()) */}
        <Box>
          <Typography variant="h6" gutterBottom>{t('resolved_title')}</Typography>
          {!isLoading && resolvedIssues.length === 0 && (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {t('resolved_empty')}
            </Typography>
          )}
          <List dense>
            {resolvedIssues.map((issue) => (
              <ListItem
                key={issue.id}
                divider
                secondaryAction={
                  <Button 
                    size="small" 
                    onClick={() => handleToggleStatus(issue, 'Aperto')}
                    color="inherit"
                  >
                    {t('reopen_button')}
                  </Button>
                }
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={issue.description}
                  secondary={`${t('resolved_on')} ${issue.createdAt ? new Date(issue.createdAt.toDate()).toLocaleString(getLocaleFormat()) : '...'}`}
                  sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Paper>
      
      <AddToWorkOrderModal
        open={modalOpen}
        onClose={handleCloseModal}
        teamId={teamId}
        vehicleId={vehicleId}
        item={selectedIssue}
        onAssign={handleAssignToWorkOrder}
        t={t} // Passa t
        currentLang={currentLang} // Passa la lingua
      />
    </>
  );
}

export default IssueLog;